import React, { useContext, useEffect, useState } from "react";
import api from "../../../utils/axios";
import {
  Box,
  Button,
  Heading,
  HStack,
  Select,
  Spinner,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from "@chakra-ui/react";
import { UserContext } from "../../../context/UserContext";
import AdminUserFilter from "../../../components/AdminUserFilter";
import AdminUserTableFooter from "../../../components/AdminUserTableFooter";
import { hasPermission, renderArrow } from "../../../utils/helpers";
import { prettyPrintLastName } from "../../../utils/PrettyPrints";

function AdminAssignDorms() {
  const { user, language } = useContext(UserContext);

  const [filteredUsers, setFilteredUsers] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageTotal, setPageTotal] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pageNumberChange, setPageNumberChange] = useState(false);

  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  const [dorms, setDorms] = useState([]);
  const [dormsFiltered, setDormsFiltered] = useState([]);
  const [dormCapacities, setDormCapacities] = useState({});

  const [dormBuilding, setDormBuilding] = useState(null);

  const [updateNewDorm, setUpdateNewDorm] = useState(null);
  const [updateOldDorm, setUpdateOldDorm] = useState(null);

  const [updateUserId, setUpdateUserId] = useState(null);

  useEffect(() => {
    api.get("/api/dorms").then((res) => {
      setDorms(res.data);
      if (user.roleChurchId?._id) {
        setDormsFiltered(
          res.data.filter((d) => d.church?._id === user.roleChurchId._id)
        );
      } else {
        if (dormBuilding) {
          setDormsFiltered(
            res.data.filter((d) => d.building?._id === dormBuilding)
          );
        }
        else
          setDormsFiltered(res.data);
      }
    });
    updateDormCapacities();
  }, [dormBuilding]);

  async function updateDormCapacities() {
    api.get("/api/dorms/capacities").then((res) => {
      setDormCapacities(res.data);
    });
  }

  function populateUserTableFields(u) {
    return {
      ...u,
      groupName: u.group != null ? u.group.name : "",
      groupLanguage: u.group != null ? u.group.language : "",
    };
  }

  function renderDormName(d) {
    if (!d) {
      return "None";
    }

    const name = d.name ? `, ${d.name}` : "";
    return `(${d.curr_occupancy}/${d.max_occupancy}) ${d.building?.name}: [Room ${d.room_number}${name}, Floor ${d.floor}]`;
  }

  async function handleUpdate(u) {
    if (!hasPermission(user, ["assign_dorms", "assign_dorms_for_church"])) {
      alert("You do not have permission to update dorms");
      return;
    }

    let userObj = u;

    userObj = {
      ...u,
      dorm: updateNewDorm || u.dorm,
    };

    if (updateNewDorm && updateNewDorm !== "None") {
      const newDorm = dorms.find((d) => d._id === updateNewDorm);
      if (newDorm.curr_occupancy >= newDorm.max_occupancy) {
        alert("Dorm is full");
        setUpdateNewDorm(updateOldDorm || "None");
        return;
      }
    }

    if (updateNewDorm === "None") {
      delete userObj.dorm;
    }

    try {
      const updatedUser = (
        await api.put(
          `/api/users/admin-table-format/assign-dorms/${u._id}`,
          userObj
        )
      ).data;

      const updatedUsers = filteredUsers.map((oldUser) => {
        if (oldUser._id === u._id) {
          return populateUserTableFields(updatedUser);
        }
        return oldUser;
      });

      setFilteredUsers(updatedUsers);

      if (updateNewDorm != null && updateNewDorm !== updateOldDorm) {
        dorms.forEach((d) => {
          if (d._id === updatedUser.dorm?._id) {
            d.curr_occupancy += 1;
          }
          if (d._id === updateOldDorm) {
            // To ensure the no. of curr_occupancy will not be negative
            (d.curr_occupancy > 0) && (d.curr_occupancy -= 1);
          }
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }

    setUpdateUserId(null);
    setUpdateOldDorm(null);
    setUpdateNewDorm(null);

    updateDormCapacities();
  }

  const handleSort = (field) => {
    const newSortOrder =
      sortBy === field && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(field);
    setSortOrder(newSortOrder);
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Heading textAlign="center" margin="20px">
        {language === "en" ? "Assign Users to Dorms" : "将用户分配到宿舍"}
      </Heading>
      {dormCapacities.buildingOccupancies &&
        dormCapacities.buildingOccupancies.map(curr => (
          <Text as="b" color='blue' key={curr.building}>
            {language === "en"
              ? `${curr.building}: Current occupancy: ${curr.currOccupancy != undefined
                ? curr.currOccupancy
                : ""
              }, Max capacity: ${curr.maxCapacity}`
              : `${curr.building}: 住宿人數: ${curr.currOccupancy != undefined
                ? curr.currOccupancy
                : ""
              }, 床位總數: ${curr.maxCapacity}`}
          </Text>
        ))
      }
      <Text as="b">
        {language === "en"
          ? `Total Dorms: Current occupancy: ${dormCapacities.currOccupancy != undefined
            ? dormCapacities.currOccupancy
            : ""
          }, Max capacity: ${dormCapacities.maxCapacity != undefined
            ? dormCapacities.maxCapacity
            : ""
          }`
          : `宿舍大樓之總和: 住宿總人數: ${dormCapacities.currOccupancy != undefined
            ? dormCapacities.currOccupancy
            : ""
          }, 床位總數: ${dormCapacities.maxCapacity != undefined
            ? dormCapacities.maxCapacity
            : ""
          }`}
      </Text>
      {/* <Text as="b">
        {language === "en"
          ? `Total: Max dorm occupancy: ${
              dormCapacities.maxCapacity != undefined
                ? dormCapacities.maxCapacity
                : ""
            }`
          : `總和: 床位總數: ${
              dormCapacities.maxCapacity != undefined
                ? dormCapacities.maxCapacity
                : ""
            }`}
      </Text> */}
      <AdminUserFilter
        setFilteredUsers={setFilteredUsers}
        setPageLoading={setPageLoading}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        pageSize={pageSize}
        setPageTotal={setPageTotal}
        pageNumberChange={pageNumberChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onlyAttendingAndAccepted
        onlyDorming
        setDormBuilding={setDormBuilding}
      />
      {pageLoading ? (
        <Spinner size="xl" />
      ) : (
        <>
          <TableContainer margin={5}>
            <Table size="sm">
              <Thead>
                <Tr style={{ cursor: "pointer" }}>
                  <Th scope="col" onClick={() => handleSort("firstName")}>
                    First Name {renderArrow("firstName", sortBy, sortOrder)}
                  </Th>
                  <Th scope="col" onClick={() => handleSort("lastName")}>
                    Last Name {renderArrow("lastName", sortBy, sortOrder)}
                  </Th>
                  <Th scope="col" onClick={() => handleSort("chineseName")}>
                    Chin. Name {renderArrow("chineseName", sortBy, sortOrder)}
                  </Th>
                  <Th scope="col" onClick={() => handleSort("familyId")}>
                    Fam. Id {renderArrow("familyId", sortBy, sortOrder)}
                  </Th>
                  <Th scope="col" onClick={() => handleSort("age")}>
                    Age {renderArrow("age", sortBy, sortOrder)}
                  </Th>
                  <Th scope="col" onClick={() => handleSort("grade")}>
                    Grade {renderArrow("grade", sortBy, sortOrder)}
                  </Th>
                  <Th scope="col" onClick={() => handleSort("gender")}>
                    Gender {renderArrow("gender", sortBy, sortOrder)}
                  </Th>
                  <Th
                    scope="col"
                    onClick={() => handleSort("resident_commuter_status")}
                  >
                    Commuting{" "}
                    {renderArrow("resident_commuter_status", sortBy, sortOrder)}
                  </Th>
                  <Th
                    scope="col"
                    onClick={() => handleSort("preferredLanguage")}
                  >
                    Preferred Lang.{" "}
                    {renderArrow("preferredLanguage", sortBy, sortOrder)}
                  </Th>
                  <Th scope="col" onClick={() => handleSort("specialNeeds")}>
                    Spc. Needs {renderArrow("specialNeeds", sortBy, sortOrder)}
                  </Th>
                  <Th
                    scope="col"
                    onClick={() => handleSort("registrationStatus")}
                  >
                    Reg. Status{" "}
                    {renderArrow("registrationStatus", sortBy, sortOrder)}
                  </Th>
                  <Th scope="col" onClick={() => handleSort("dorm")}>
                    Dorm {renderArrow("dorm", sortBy, sortOrder)}
                  </Th>
                  <Th scope="col"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredUsers &&
                  filteredUsers.map((u) => (
                    <Tr
                      key={u._id}
                      backgroundColor={
                        u._id != updateUserId
                          ? ""
                          : updateNewDorm === updateOldDorm
                            ? ""
                            : "lightblue"
                      }
                    >
                      <Td>{u.firstName}</Td>
                      <Td>{prettyPrintLastName(u)}</Td>
                      <Td>{u.chineseName}</Td>
                      <Td>{u.familyId}</Td>
                      <Td>{u.age}</Td>
                      <Td>{u.grade}</Td>
                      <Td>{u.gender}</Td>
                      <Td>
                        {u.resident_commuter_status?.includes("Commuting")
                          ? "Yes"
                          : "No"}
                      </Td>
                      <Td>{u.preferredLanguage}</Td>
                      <Td>{u.hasSpecialNeeds ? "Yes" : "No"}</Td>
                      <Td>{u.registrationStatus}</Td>
                      <Td>
                        <Select
                          value={
                            u._id === updateUserId
                              ? updateNewDorm || u.dorm?._id
                              : u.dorm?._id || "None"
                          }
                          style={{
                            color:
                              u.dorm &&
                                !dormsFiltered.some(
                                  (d) => d._id === u.dorm._id
                                ) &&
                                (!updateNewDorm || updateNewDorm === u.dorm._id)
                                ? "red"
                                : "black",
                            minWidth: "max-content",
                          }}
                          onChange={async (e) => {
                            setUpdateNewDorm(e.target.value);
                            setUpdateOldDorm(u.dorm?._id);
                            setUpdateUserId(u._id);
                          }}
                        >
                          <option value="None" style={{ color: "black" }}>
                            None
                          </option>
                          {u.dorm &&
                            !dormsFiltered.some(
                              (d) => d._id === u.dorm._id
                            ) && (
                              <option
                                value={u.dorm?._id}
                                style={{ color: "red" }}
                              >
                                {renderDormName(
                                  dorms.find((d) => d._id === u.dorm?._id)
                                )}
                              </option>
                            )}
                          {dormsFiltered &&
                            dormsFiltered.map((d) => {
                              return (
                                <option
                                  value={d._id}
                                  key={d._id}
                                  style={{ color: "black" }}
                                >
                                  {renderDormName(d)}
                                </option>
                              );
                            })}
                        </Select>
                      </Td>
                      <Td>
                        <Button
                          colorScheme={"green"}
                          isDisabled={
                            !hasPermission(user, [
                              "assign_dorms",
                              "assign_dorms_for_church",
                            ]) || u._id != updateUserId
                              ? true
                              : updateNewDorm === updateOldDorm
                                ? true
                                : false
                          }
                          onClick={() => handleUpdate(u)}
                        >
                          Update
                        </Button>
                      </Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
          </TableContainer>
          <AdminUserTableFooter
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            pageSize={pageSize}
            setPageSize={setPageSize}
            pageTotal={pageTotal}
            pageNumberChange={pageNumberChange}
            setPageNumberChange={setPageNumberChange}
          />
        </>
      )}
    </Box>
  );
}

export default AdminAssignDorms;
